import { useEffect, useState } from "react";

import { Drawer, Portal, Heading, Box, Spinner } from "@/storybook-components";

const ContentDrawer = ({
  open,
  onClose,
  header,
  content,
  footer,
  trapFocus,
  zIndex,
  onCloseComplete,
  dataTestId = "ideas-drawer"
}): JSX.Element => {
  const [loading, setIsLoading] = useState(true);

  useEffect(() => {
    let timer;
    if (open) {
      timer = setTimeout(() => {
        setIsLoading(false);
      }, 1500);
    } else {
      setIsLoading(true);
    }

    return () => {
      // eslint-disable-next-line no-undef
      clearTimeout(timer);
    };
  }, [open]);

  const handleOpenChange = (details: { open: boolean }) => {
    if (!details.open) {
      onClose?.();
    }
  };

  return (
    <Drawer.Root
      open={open}
      placement="end"
      variant="primary"
      onOpenChange={handleOpenChange}
      onExitComplete={() => onCloseComplete?.()}
      trapFocus={trapFocus || false}
      data-testid={dataTestId}
    >
      <Portal>
        <Drawer.Backdrop css={{ "--drawer-z-index": zIndex }} />
        <Drawer.Positioner css={{ "--drawer-z-index": zIndex }}>
          <Drawer.Content
            bg="gray.100"
            boxShadow="none"
            py={6}
            data-testid={`${dataTestId}-content`}
          >
            <Drawer.CloseTrigger
              color="gray.800"
              mt={2}
              data-testid={`${dataTestId}-close`}
            />

            <Drawer.Header
              px={4}
              pt={0}
              pb={6}
              fontSize="md"
              fontWeight="normal"
              data-testid={`${dataTestId}-header`}
            >
              <Heading
                as="h2"
                size="md"
                fontSize="md"
                fontWeight="semibold"
                lineHeight="shorter"
                m={0}
              >
                {header}
              </Heading>
            </Drawer.Header>

            <Drawer.Body
              w="full"
              p={0}
              px={0}
              data-testid={`${dataTestId}-body`}
            >
              {loading ? (
                <Box
                  px={4}
                  h="90%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color="gray.300"
                >
                  <Spinner size={{ base: "lg", md: "xl" }} />
                </Box>
              ) : (
                content
              )}
            </Drawer.Body>

            {footer && (
              <Drawer.Footer px={0} pt={6} pb={0}>
                {footer}
              </Drawer.Footer>
            )}
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};

export default ContentDrawer;
